import { shiftModalRoutingService } from 'modals/ShiftModal/ShiftModal.ui.context';
import { useFormBehaviors, useGQLQuery } from 'hooks';

import {
  UseFormBehaviors,
  UseFormBehaviors_ValidationOptionsType,
} from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useTranslation } from 'hooks/useTranslation';
import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { API_POST_releasePublishedShifts } from 'requests/POST_releaseShift';
import { toastService } from 'services';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { handleGenericErrorMessage, firstArrayValue } from 'utils/handleGenericErrorMessage';
import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';
import { ReleaseWithTimeOffPayloadType } from 'requests/index.types';
import {
  defaultEndTime,
  defaultStartTime,
  fetchTimeOffCustomFieldsQueryConfig,
  titleOptions,
} from 'modals/TimeOffModal/TimeOffModal.constants';
import {
  VALIDATION_durationMin1Hr,
  VALIDATION_endDate,
  VALIDATION_without_msg,
} from 'modals/TimeOffModal/TimeOffModalView.validation.constants';
import { combineMsgs } from 'validations/index.utils';
import { DateTime } from 'luxon';
import { CustomField } from 'types/graphql';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { useCanManageSkillsSelector } from 'store/selectors/skillsSelectors/canManageSkillsSelector';
import { SkillOptionType } from 'components/SkillsSelectInput';
import { useSelector } from 'react-redux';
import { isShiftBiddingFeatureEnabledSelector } from 'store/selectors/featureFlagSelectors';
import { FEATURE_SELECTORS_MAP } from 'guards/FeatureGuard.constants';
import {
  getDefaultAllowOvertime,
  getIsBiddingPosition,
  getBiddingOvertimeDefault,
} from 'utils/shiftFormEvents';

export type ReleaseType = {
  reason_category: string;
  reason_message: string;
  recurring: string;
  dontAddTimeOff: boolean;
  required_skills: string[];
  allow_overtime: boolean;
  notes: string;
  title: string[];
  pay_code_id: string[];
  allDay: boolean;
  startDate: string[];
  startTime: string[];
  endDate: string[];
  endTime: string[];
  [key: string]: string | string[] | boolean | undefined;
};

type ReleaseShiftViewContextType = UseFormBehaviors<ReleaseType> & {
  submitRelease: (timeOffPayload?: ReleaseWithTimeOffPayloadType) => Promise<void>;
  isSubmitting: boolean;
  isDisabled: boolean;
  timeOffCustomFields: CustomField[];
  timeOffTimeZone?: string;
  isSkillsCriteriaEditable: boolean;
  isAllowOvertimeEditable: boolean;
  communitySkillOptions: SkillOptionType[];
};

export const ReleaseShiftViewContext = createContext({} as ReleaseShiftViewContextType);

type SkillsCriteriaOrder = {
  biddable?: boolean | null;
  isExternal?: boolean | null;
  sentToAgencies?: boolean | null;
};

export const getIsSkillsCriteriaEditable = (
  canManageSkills: boolean,
  order?: SkillsCriteriaOrder | null,
): boolean => canManageSkills && !order?.biddable && !order?.isExternal && !order?.sentToAgencies;

type ReleaseShiftViewProviderProps = {
  children: React.ReactNode;
  isConflictedRelease?: boolean;
  communitySkillOptions: SkillOptionType[];
};

export const ReleaseShiftViewProvider: React.FC<ReleaseShiftViewProviderProps> = ({
  children,
  isConflictedRelease = false,
  communitySkillOptions,
}) => {
  const { apiRequest } = useAPIActions();
  const { data } = useShiftModalDataContext();
  const order = data?.order;

  const selectedLocation = getCurrentlySelectedLocation();

  const { refreshBackgroundPage } = useShiftModalPropsContext();
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mainJanePrimaryCommunityId = Number(order?.mainJane?.primaryCommunity?.id);
  const isCustomFieldsEnabled =
    useSelector(FEATURE_SELECTORS_MAP['FEATURE_TYPE_CUSTOM_FIELDS']) && !order?.isExternal;
  const { data: fetchedTimeOffCustomFields, fetch: fetchTimeOffCustomFields } = useGQLQuery(
    fetchTimeOffCustomFieldsQueryConfig({ byFacilityId: mainJanePrimaryCommunityId }),
  );

  useEffect(() => {
    if (isCustomFieldsEnabled && !!mainJanePrimaryCommunityId) fetchTimeOffCustomFields();
  }, [isCustomFieldsEnabled, mainJanePrimaryCommunityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const timeOffCustomFields = useMemo<CustomField[]>(
    () => Object.values(fetchedTimeOffCustomFields ?? {}) as CustomField[],
    [fetchedTimeOffCustomFields],
  );

  const shiftStartAt = order?.startAt;
  const shiftEndAt = order?.endAt;
  const time_zone =
    order?.mainJane?.primaryCommunity?.timeZone ?? selectedLocation?.timeZone ?? undefined;

  const defaultDate = useMemo(() => {
    if (!shiftStartAt) return DateTime.now().toString();
    const rawStartAt = DateTime.fromISO(shiftStartAt, { zone: 'utc' });
    const startAtWithTimeZone = time_zone ? rawStartAt.setZone(time_zone) : rawStartAt;
    return startAtWithTimeZone.setZone('local', { keepLocalTime: true }).toString();
  }, [shiftStartAt, time_zone]);

  const defaultEndDate = useMemo(() => {
    if (!shiftEndAt) return undefined;
    const rawEndAt = DateTime.fromISO(shiftEndAt, { zone: 'utc' });
    const endAtWithTimeZone = time_zone ? rawEndAt.setZone(time_zone) : rawEndAt;
    return endAtWithTimeZone.setZone('local', { keepLocalTime: true }).toString();
  }, [shiftEndAt, time_zone]);

  const defaultStartAt = useMemo(() => {
    if (!shiftStartAt) return undefined;
    const rawStartAt = DateTime.fromISO(shiftStartAt, { zone: 'utc' });
    const startAtWithTimeZone = time_zone ? rawStartAt.setZone(time_zone) : rawStartAt;
    return startAtWithTimeZone.toFormat('HH:mm:ss');
  }, [shiftStartAt, time_zone]);

  const defaultEndAt = useMemo(() => {
    if (!shiftEndAt) return undefined;
    const rawEndAt = DateTime.fromISO(shiftEndAt, { zone: 'utc' });
    const endAtWithTimeZone = time_zone ? rawEndAt.setZone(time_zone) : rawEndAt;
    return endAtWithTimeZone.toFormat('HH:mm:ss');
  }, [shiftEndAt, time_zone]);

  const canManageSkills = useCanManageSkillsSelector();
  const isSkillsCriteriaEditable = getIsSkillsCriteriaEditable(canManageSkills, order);

  const shiftSkillIds = useMemo(
    () => (order?.ordersSkills ?? []).map(({ skill }) => `${skill.displayId}`),
    [order?.ordersSkills],
  );
  const memberDefaultSkillIds = useMemo(() => {
    if (shiftSkillIds.length) return [];
    const communitySkillIds = new Set(communitySkillOptions.map((option) => option.value));
    return (order?.mainJane?.skills ?? [])
      .map((skill) => `${skill.displayId}`)
      .filter((skillId) => communitySkillIds.has(skillId));
  }, [shiftSkillIds, communitySkillOptions, order?.mainJane?.skills]);
  const skillsTouched = useRef(memberDefaultSkillIds.length > 0);
  const isShiftBiddingFeatureEnabled = useSelector(isShiftBiddingFeatureEnabledSelector);
  const isAllowOvertimeEditable = !order?.isExternal;
  const initialAllowOvertime = getDefaultAllowOvertime({
    isBidding: getIsBiddingPosition(order?.position?.openShiftPolicy, isShiftBiddingFeatureEnabled),
    biddingOvertimeDefault: getBiddingOvertimeDefault(order?.community?.biddingConfiguration),
    fallback: !!order?.allowOvertime,
  });
  const handleRelease = useCallback(
    async (values: ReleaseType, timeOffPayload?: ReleaseWithTimeOffPayloadType) => {
      const shiftIdToRelease = order?.id;
      if (!shiftIdToRelease) return;

      const { required_skills, allow_overtime, ...releaseValues } = values;
      const isSkillsChanged =
        skillsTouched.current &&
        [...required_skills].sort().join(',') !== [...shiftSkillIds].sort().join(',');

      setIsSubmitting(true);

      try {
        await apiRequest(
          API_POST_releasePublishedShifts({
            ...releaseValues,
            shift_id: shiftIdToRelease,
            time_off: timeOffPayload,
            ...(isAllowOvertimeEditable && { allow_overtime }),
            ...(isSkillsCriteriaEditable &&
              isSkillsChanged && { required_skill_ids: required_skills.map(Number) }),
          }),
          {
            onSuccess: () => {
              toastService.success(t('success:SHIFT_RELEASED'));
              refreshBackgroundPage();

              if (isConflictedRelease) {
                shiftModalRoutingService.goBackToView('AwardTeamMemberView', {
                  conflictedShiftId: null,
                  conflictedShiftCommunityId: null,
                });
              } else {
                shiftModalRoutingService.close();
              }
            },
            onError: (e) => {
              const nestedMessage =
                e?.data?.error_messages && typeof e.data.error_messages === 'object'
                  ? firstArrayValue(e.data.error_messages)
                  : null;

              const errorToHandle = nestedMessage
                ? { ...e, data: { ...e.data, message: nestedMessage } }
                : e;

              handleGenericErrorMessage(
                errorToHandle,
                t('errors:SOMETHING_WENT_WRONG_RELEASING_SHIFT'),
              );
            },
          },
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      apiRequest,
      refreshBackgroundPage,
      order?.id,
      t,
      isConflictedRelease,
      shiftSkillIds,
      isSkillsCriteriaEditable,
      isAllowOvertimeEditable,
    ],
  );

  const formUtils = useFormBehaviors<ReleaseType>({
    initialState: {
      reason_category: '',
      reason_message: '',
      recurring: '',
      dontAddTimeOff: false,
      required_skills: shiftSkillIds.length ? shiftSkillIds : memberDefaultSkillIds,
      allow_overtime: initialAllowOvertime,
      notes: '',
      title: [titleOptions[0].value],
      pay_code_id: [],
      allDay: true,
      startDate: [defaultDate],
      startTime: [defaultStartAt || defaultStartTime],
      endDate: [defaultEndDate || defaultDate],
      endTime: [defaultEndAt || defaultEndTime],
    },
    validations: {
      startDate: VALIDATION_without_msg,
      startTime: VALIDATION_without_msg,
      endDate: VALIDATION_endDate,
      endTime: combineMsgs(VALIDATION_without_msg, VALIDATION_durationMin1Hr),
    } as unknown as UseFormBehaviors_ValidationOptionsType<ReleaseType>,
    onSubmit: handleRelease,
    isDirtyCheckEnabled: true,
  });

  useEffect(() => {
    if (!timeOffCustomFields.length) return;
    timeOffCustomFields.forEach((field) => {
      if (formUtils.values[field.name] !== undefined) return;
      const shouldPrefill =
        field.fieldType === 'dropdown' && field.prefillDefault && field.customFieldOptions?.length;
      let defaultValue: string | string[];
      if (shouldPrefill) {
        defaultValue = [field.customFieldOptions?.[0]?.value ?? ''];
      } else if (field.fieldType === 'dropdown') {
        return;
      } else {
        defaultValue = '';
      }
      formUtils.onChange({ target: { name: field.name, value: defaultValue } });
    });
  }, [timeOffCustomFields, formUtils]);

  const handleChange = useCallback(
    (event) => {
      if (Array.isArray(event)) return formUtils.onChange(event);
      const newValue = { ...event };
      if (event.target.name === 'required_skills') skillsTouched.current = true;
      if (event.target.name === 'reason_category') newValue.target.value = newValue.target.value[0];
      formUtils.onChange(newValue);
    },
    [formUtils],
  );

  const isDisabled =
    formUtils.values.reason_category === '' ||
    (formUtils.values.reason_category === 'other' && formUtils.values.reason_message === '');

  const submitRelease = useCallback(
    async (timeOffPayload?: ReleaseWithTimeOffPayloadType) => {
      await handleRelease(formUtils.values, timeOffPayload);
    },
    [formUtils.values, handleRelease],
  );

  const value = useMemo(() => {
    return {
      ...formUtils,
      onChange: handleChange,
      isDisabled,
      submitRelease,
      isSubmitting,
      timeOffCustomFields,
      timeOffTimeZone: time_zone,
      isSkillsCriteriaEditable,
      isAllowOvertimeEditable,
      communitySkillOptions,
    };
  }, [
    formUtils,
    handleChange,
    isDisabled,
    submitRelease,
    isSubmitting,
    timeOffCustomFields,
    time_zone,
    isSkillsCriteriaEditable,
    isAllowOvertimeEditable,
    communitySkillOptions,
  ]);

  return (
    <ReleaseShiftViewContext.Provider value={value}>{children}</ReleaseShiftViewContext.Provider>
  );
};

export const useReleaseShiftViewContext = (): ReleaseShiftViewContextType =>
  useContext(ReleaseShiftViewContext);
